// v2.0.0 → v2.13.0 — Page /addressing : Mapbox Standard 3D (fallback Leaflet) +
// génération d'adresse virtuelle + inscription commerce.
// Migration carte : Leaflet → Mapbox 3D (pitch 45°, marker user draggable).

import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import AddressingMapMapbox from "@/pages/AddressingMapMapbox";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const ABIDJAN_CENTER = [5.36, -4.0083];

function isValidMapboxToken(t) {
  return typeof t === "string" && /^pk\./.test(t) && t.length > 20;
}

function FlyToControl({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && typeof target[0] === "number" && typeof target[1] === "number") {
      map.flyTo(target, 17, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

export default function AddressingPage() {
  const [communes, setCommunes] = useState([]);
  const [commerces, setCommerces] = useState({ type: "FeatureCollection", features: [] });
  const [userPos, setUserPos] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [loadingGen, setLoadingGen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  // v2.13.0 — Mapbox vs Leaflet fallback.
  const [forceLeaflet, setForceLeaflet] = useState(false);
  // v2.14.1 — Live preview (drag debounce 300ms, GET /api/v2/addressing/preview).
  const [preview, setPreview] = useState(null); // { address_civ, commune, street_name, distance_m, lat, lng } | { out_of_zone:true }
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimerRef = useRef(null);
  const previewSpinnerTimerRef = useRef(null);
  const useMapbox = !forceLeaflet && isValidMapboxToken(MAPBOX_TOKEN);
  const reloadTimerRef = useRef(null);

  // Bootstrap : communes + commerces.
  useEffect(() => {
    fetch(`${API}/v2/addressing/communes`).then((r) => r.json()).then((d) => {
      if (d?.communes) setCommunes(d.communes);
    }).catch(() => {});
    const fetchCommerces = () =>
      fetch(`${API}/v2/commerces/public.geojson`).then((r) => r.json()).then(setCommerces).catch(() => {});
    fetchCommerces();
    reloadTimerRef.current = setInterval(fetchCommerces, 30000);
    return () => {
      if (reloadTimerRef.current) clearInterval(reloadTimerRef.current);
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      if (previewSpinnerTimerRef.current) clearTimeout(previewSpinnerTimerRef.current);
    };
  }, []);

  // Si query ?lat=&lng= dans l'URL, focus dessus.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const lat = parseFloat(sp.get("lat"));
      const lng = parseFloat(sp.get("lng"));
      if (Number.isFinite(lat) && Number.isFinite(lng)) setFlyTo([lat, lng]);
    } catch (_) {}
  }, []);

  // Haversine simple côté front pour trouver la rue la plus proche.
  const haversine = useCallback((a, b, c, d) => {
    const R = 6371000;
    const dLat = ((c - a) * Math.PI) / 180;
    const dLon = ((d - b) * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos((a * Math.PI) / 180) * Math.cos((c * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
  }, []);

  const findNearestStreet = useCallback((lat, lon) => {
    if (!communes.length) return null;
    let best = null;
    let bestD = Infinity;
    for (const c of communes) {
      for (const s of c.streets || []) {
        const d = haversine(lat, lon, s.start_lat, s.start_lon);
        if (d < bestD) { bestD = d; best = { ...s, commune: c.name }; }
      }
    }
    return best;
  }, [communes, haversine]);

  // v2.14.2 — Helper fallback Plateau-Abidjan partagé.
  const fallbackToPlateau = useCallback(() => {
    const FALLBACK_LAT = 5.3266;
    const FALLBACK_LNG = -4.0173;
    setUserPos([FALLBACK_LAT, FALLBACK_LNG]);
    setFlyTo([FALLBACK_LAT, FALLBACK_LNG]);
    setPreview({ out_of_zone: true, message: "📍 Position par défaut : Plateau, Abidjan (active la géoloc pour ta vraie position)" });
  }, []);

  const handleGenerate = async () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non supportée — bascule sur Plateau-Abidjan par défaut.");
      fallbackToPlateau();
      return;
    }
    setLoadingGen(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        await generateForCoords(lat, lon);
      },
      (err) => {
        setLoadingGen(false);
        toast.error("Géolocalisation refusée — bascule sur Plateau-Abidjan par défaut.");
        fallbackToPlateau();
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  // v2.13.0 — Réutilisable depuis drag marker.
  // v2.14.2 — Fix : si lat/lng hors zone CI, abort proprement (ne pas appeler /generate
  // qui retournerait 422). Sinon vérifier la réponse pour gérer le rejet serveur.
  const generateForCoords = useCallback(async (lat, lon) => {
    setUserPos([lat, lon]);
    if (lat < 5.0 || lat > 5.7 || lon < -4.6 || lon > -3.7) {
      setLoadingGen(false);
      toast.error("❌ Position hors zone Côte d'Ivoire — bascule sur Plateau-Abidjan par défaut.");
      // Fallback Plateau-Abidjan
      const fallbackLat = 5.3266;
      const fallbackLng = -4.0173;
      setUserPos([fallbackLat, fallbackLng]);
      setFlyTo([fallbackLat, fallbackLng]);
      setPreview({ out_of_zone: true, message: "Position hors CI → marker placé sur Plateau (Abidjan)" });
      return;
    }
    const nearest = findNearestStreet(lat, lon);
    if (!nearest) {
      setLoadingGen(false);
      toast.error("Aucune rue connue à proximité");
      return;
    }
    try {
      const r = await fetch(`${API}/v2/addressing/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          latitude: lat,
          longitude: lon,
          commune: nearest.commune,
          street_id: nearest.street_id,
          street_name: nearest.nom,
          start_lat: nearest.start_lat,
          start_lon: nearest.start_lon,
        }),
      });
      const d = await r.json();
      if (r.status === 422 && d?.reason === "out_of_zone_ci") {
        toast.error("❌ " + (d.message || "Position hors zone Côte d'Ivoire"));
        setGenerated(null);
      } else if (d?.ok) {
        setGenerated({ ...d.address, commune: nearest.commune, street_id: nearest.street_id });
        setFlyTo([lat, lon]);
        toast.success("Adresse générée ✓");
      } else {
        toast.error(d?.error || "Erreur génération");
      }
    } catch (e) {
      toast.error(e.message || "Erreur réseau");
    } finally {
      setLoadingGen(false);
    }
  }, [findNearestStreet]);

  // v2.14.1 — Drag-end marker → preview LIVE (GET, debounce 300ms, sans persistance).
  // L'utilisateur doit toujours cliquer "Générer adresse" pour persister officiellement.
  const handleUserDrag = useCallback((lat, lng) => {
    setUserPos([lat, lng]);
    // Spinner après 200ms de fetch pour ne pas flasher sur les requêtes < 200ms.
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (previewSpinnerTimerRef.current) clearTimeout(previewSpinnerTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      previewSpinnerTimerRef.current = setTimeout(() => setPreviewLoading(true), 200);
      try {
        const r = await fetch(`${API}/v2/addressing/preview?lat=${lat}&lng=${lng}`);
        const d = await r.json();
        clearTimeout(previewSpinnerTimerRef.current);
        setPreviewLoading(false);
        if (d.ok) {
          setPreview({
            address_civ: d.address_civ,
            commune: d.commune,
            street_name: d.street_name,
            distance_m: d.distance_m,
            lat: d.lat,
            lng: d.lng,
          });
        } else if (d.reason === "out_of_zone_ci") {
          setPreview({ out_of_zone: true, message: d.message || "Hors zone Grand Abidjan" });
        } else {
          setPreview({ error: true, message: d.reason || "Position invalide" });
        }
      } catch (e) {
        clearTimeout(previewSpinnerTimerRef.current);
        setPreviewLoading(false);
        setPreview({ error: true, message: "Réseau indisponible" });
      }
      // Auto-clear spinner si rien de neuf après 2s (cf brief T1-T3).
      setTimeout(() => setPreviewLoading(false), 2000);
    }, 300);
  }, []);

  // Click sur commande commerce (popup).
  const handleCommerceOrder = useCallback(async (f) => {
    const fid = f.properties.id_etablissement || f.properties.id;
    try {
      const r = await fetch(`${API}/v2/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          montant: 3500,
          client_nom: "Client Test",
          client_telephone: "+22500000000",
          id_commande: `CMD_${Date.now()}`,
          commerce_id: fid,
        }),
      });
      const d = await r.json();
      if (d?.ok) {
        toast.success("Paiement initié — redirection mock");
        window.open(d.url_paiement, "_blank", "noopener");
      } else {
        toast.error(d?.error || "Erreur paiement");
      }
    } catch (e) { toast.error(e.message); }
  }, []);

  return (
    <div className="civ-addr-page" data-testid="addressing-page">
      <header className="civ-addr-header">
        <a href="/" className="civ-addr-back" data-testid="addressing-back">← Chat</a>
        <h1>📍 Adressage CIV-AI</h1>
        <p>Génère ton adresse métrique unique sur les 13 communes du Grand Abidjan.</p>
        {useMapbox && (
          <span data-testid="addressing-engine-badge" style={{
            position: "absolute", top: 12, right: 12, fontSize: 11, padding: "3px 8px",
            background: "rgba(59,130,246,0.18)", border: "1px solid rgba(59,130,246,0.4)",
            color: "#93C5FD", borderRadius: 999, fontWeight: 600,
          }}>🛰 Mapbox 3D</span>
        )}
      </header>

      <div className="civ-addr-map-wrap" data-testid="addressing-map-wrap" data-engine={useMapbox ? "mapbox" : "leaflet"}>
        {/* v2.14.1 — Overlay preview live (drag debounce 300ms). */}
        {(preview || previewLoading) && (
          <div
            data-testid="addressing-preview-overlay"
            data-state={previewLoading ? "loading" : (preview?.out_of_zone ? "out_of_zone" : (preview?.error ? "error" : "ok"))}
            style={{
              position: "absolute", top: 12, left: 12, zIndex: 1000,
              background: "rgba(15,23,42,0.92)",
              backdropFilter: "blur(8px)",
              color: "#F1F5F9",
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              fontSize: 12,
              maxWidth: 280,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>
              📍 Preview adresse {previewLoading && <span data-testid="addressing-preview-spinner" style={{ marginLeft: 6 }}>🔄 Recherche…</span>}
            </div>
            {!previewLoading && preview && preview.out_of_zone && (
              <div style={{ color: "#FB923C", marginTop: 4, fontWeight: 600 }}>📍 Hors zone Côte d'Ivoire</div>
            )}
            {!previewLoading && preview && preview.error && (
              <div style={{ color: "#F87171", marginTop: 4 }}>⚠ {preview.message}</div>
            )}
            {!previewLoading && preview && preview.address_civ && (
              <>
                <code data-testid="addressing-preview-code" style={{ display: "block", fontSize: 13, color: "#93C5FD", marginTop: 4, fontWeight: 700 }}>{preview.address_civ}</code>
                <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                  {preview.street_name} · <span style={{ color: "#34D399" }}>{preview.commune}</span> · {preview.distance_m}m
                </div>
              </>
            )}
          </div>
        )}
        {useMapbox ? (
          <AddressingMapMapbox
            token={MAPBOX_TOKEN}
            abidjanCenter={ABIDJAN_CENTER}
            userPos={userPos}
            onUserPosChange={handleUserDrag}
            commerces={commerces}
            flyTo={flyTo}
            onCommerceOrder={handleCommerceOrder}
            onTokenInvalid={() => { setForceLeaflet(true); toast.message("Mapbox indisponible → Leaflet fallback"); }}
          />
        ) : (
          <MapContainer
            center={ABIDJAN_CENTER}
            zoom={12}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            {flyTo && <FlyToControl target={flyTo} />}
            {userPos && (
              <Marker
                position={userPos}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const ll = e.target.getLatLng();
                    handleUserDrag(ll.lat, ll.lng);
                  },
                }}
              >
                <Popup>📍 Glisse pour ajuster<br />{preview?.address_civ || generated?.id_adresse_unique || ""}</Popup>
              </Marker>
            )}
            {(commerces.features || []).map((f) => {
              const [lng, lat] = f.geometry?.coordinates || [];
              if (typeof lat !== "number" || typeof lng !== "number") return null;
              const fid = f.properties.id_etablissement || f.properties.id;
              return (
                <Marker key={fid} position={[lat, lng]}>
                  <Popup>
                    <strong>{f.properties.nom}</strong>
                    <br />
                    {f.properties.categorie}
                    <br />
                    <code>{f.properties.adresse_virtuelle}</code>
                    <br />
                    <button
                      type="button"
                      onClick={() => handleCommerceOrder(f)}
                      data-testid={`commerce-order-${fid}`}
                      style={{ marginTop: 6, padding: "4px 10px", cursor: "pointer", background: "#1e293b", color: "#fff", border: "none", borderRadius: 4 }}
                    >🛒 Commander</button>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>

      <div className="civ-addr-panel" data-testid="addressing-panel">
        <button
          type="button"
          className="civ-addr-gen-btn"
          onClick={handleGenerate}
          disabled={loadingGen}
          data-testid="addressing-generate-btn"
        >
          {loadingGen ? "⏳ Localisation…" : "📍 Générer mon adresse métrique"}
        </button>
        {useMapbox && userPos && (
          <small data-testid="addressing-drag-hint" style={{ display: "block", marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
            💡 Glisse le marker rouge pour ajuster la position.
          </small>
        )}
        {generated && (
          <div className="civ-addr-result" data-testid="addressing-result">
            <div className="civ-addr-result-code">{generated.id_adresse_unique}</div>
            <div className="civ-addr-result-text">{generated.adresse_textuelle}</div>
            <button
              type="button"
              className="civ-addr-register-btn"
              onClick={() => setRegisterOpen(true)}
              data-testid="addressing-open-register"
            >🏪 Inscrire mon commerce ici</button>
          </div>
        )}
      </div>

      {registerOpen && (
        <RegisterCommerceModal
          generated={generated}
          userPos={userPos}
          onClose={() => setRegisterOpen(false)}
          onSuccess={(feature) => {
            setCommerces((prev) => ({
              ...prev,
              features: [...(prev.features || []), feature],
            }));
            setRegisterOpen(false);
            toast.success("Commerce enregistré ✓");
          }}
        />
      )}
    </div>
  );
}

function RegisterCommerceModal({ generated, userPos, onClose, onSuccess }) {
  const [nom, setNom] = useState("");
  const [categorie, setCategorie] = useState("restaurant");
  const [contact, setContact] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!nom.trim()) { toast.error("Nom requis"); return; }
    if (!generated || !userPos) { toast.error("Génère d'abord ton adresse"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/v2/commerces/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: nom.trim(),
          categorie,
          contact: contact.trim(),
          description_nouchi: desc.trim(),
          // v2.0.1 — shape canonique nesté.
          location: {
            latitude: userPos[0],
            longitude: userPos[1],
            commune: generated.commune,
            street_id: generated.street_id,
          },
        }),
      });
      const d = await r.json();
      if (d?.ok) onSuccess(d.feature);
      else toast.error(d?.error || "Erreur enregistrement");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="civ-addr-modal-backdrop" onClick={onClose} data-testid="commerce-register-modal">
      <div className="civ-addr-modal" onClick={(e) => e.stopPropagation()}>
        <h2>🏪 Inscrire mon commerce</h2>
        <p className="civ-addr-modal-sub">Adresse virtuelle : <code>{generated?.id_adresse_unique || "(génère d'abord)"}</code></p>
        <label>Nom du commerce<input value={nom} onChange={(e) => setNom(e.target.value)} data-testid="commerce-form-nom" /></label>
        <label>Catégorie
          <select value={categorie} onChange={(e) => setCategorie(e.target.value)} data-testid="commerce-form-cat">
            <option value="restaurant">🍽️ Restaurant</option>
            <option value="boutique">🛍️ Boutique</option>
            <option value="pharmacie">💊 Pharmacie</option>
            <option value="service">🔧 Service</option>
            <option value="autre">📍 Autre</option>
          </select>
        </label>
        <label>Contact (téléphone)<input value={contact} onChange={(e) => setContact(e.target.value)} data-testid="commerce-form-contact" /></label>
        <label>Description (Nouchi OK)<textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="commerce-form-desc" /></label>
        <div className="civ-addr-modal-actions">
          <button type="button" onClick={onClose} className="civ-addr-modal-cancel" data-testid="commerce-form-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={busy} className="civ-addr-modal-submit" data-testid="commerce-form-submit">
            {busy ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
